// Auth context for managing authentication state
// BR-52: Entitled wraps every gated feature

import React, { createContext, useContext, useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PlatformModuleId, WorkspaceProfile, WorkspaceProfileId, QuickAction } from '@shared/constants/platform';

export const API_BASE = import.meta.env.VITE_API_URL || 'https://kanvara-api.reshigan-085.workers.dev';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tier: string;
  organisationId: string;
  industryId: string;
  mfaEnabled: boolean;
}

interface Entitlement {
  plan: string;
  modules: Set<string>;
  tier: string;
}

/** Nav + home screen composed server-side by GET /me from the user's workspace. */
export interface Workspace {
  status: 'pending' | 'active' | 'pending_approval' | string;
  profile: WorkspaceProfileId | null;
  modules: PlatformModuleId[];
  nav: { path: string; label: string }[];
  home: string;
  profileName: string | null;
  eyebrow: string | null;
  title: string | null;
  quickActions: QuickAction[];
}

export interface MeResponse {
  id: string;
  email: string;
  name: string;
  role: string;
  tier: string;
  organisation_id: string;
  acting_organisation_id?: string;
  workspace: Workspace;
  platformModules: PlatformModuleId[];
  eligibleProfiles: WorkspaceProfile[];
}

interface AuthState {
  user: User | null;
  token: string | null;
  ent: Entitlement | null;
  isAuthenticated: boolean;
  mfaRequired: boolean;
  challengeId: string | null;
  stepUpRequired: boolean;
  forcePasswordChange: boolean;
  workspace: Workspace | null;
  platformModules: PlatformModuleId[];
  eligibleProfiles: WorkspaceProfile[];
  tenantOverride: string | null;
  login: (user: User, token: string, forcePasswordChange?: boolean) => void;
  setMfaRequired: (required: boolean, challengeId?: string) => void;
  setStepUpRequired: (required: boolean) => void;
  setForcePasswordChange: (v: boolean) => void;
  setEntitlement: (ent: Entitlement) => void;
  setWorkspace: (workspace: Workspace | null, platformModules?: PlatformModuleId[], eligibleProfiles?: WorkspaceProfile[]) => void;
  setTenantOverride: (orgId: string | null) => void;
  can: (action: string) => boolean;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      ent: null,
      isAuthenticated: false,
      mfaRequired: false,
      challengeId: null,
      stepUpRequired: false,
      forcePasswordChange: false,
      workspace: null,
      platformModules: [],
      eligibleProfiles: [],
      tenantOverride: null,

      login: (user, token, forcePasswordChange = false) => {
        set({ user, token, isAuthenticated: true, mfaRequired: false, challengeId: null, forcePasswordChange });
        // Nav, home screen and module entitlements all come from /me.
        void refreshMe();
      },

      setWorkspace: (workspace, platformModules, eligibleProfiles) => {
        set(state => ({
          workspace,
          platformModules: platformModules ?? state.platformModules,
          eligibleProfiles: eligibleProfiles ?? state.eligibleProfiles,
        }));
      },

      setTenantOverride: (orgId) => {
        set({ tenantOverride: orgId });
        // Entitlements and nav belong to the tenant being acted on.
        void refreshMe();
      },

      setForcePasswordChange: (v) => set({ forcePasswordChange: v }),

      setMfaRequired: (required, challengeId) => {
        set({ mfaRequired: required, challengeId: challengeId || null });
      },

      setStepUpRequired: (required) => {
        set({ stepUpRequired: required });
      },

      setEntitlement: (ent) => {
        set({ ent });
      },

      can: (action) => {
        const ent = get().ent;
        if (!ent) return false;
        const [module] = action.split(':');
        return ent.modules?.has(module as any) ?? false;
      },

      logout: () => {
        set({
          user: null,
          token: null,
          ent: null,
          isAuthenticated: false,
          mfaRequired: false,
          challengeId: null,
          forcePasswordChange: false,
          workspace: null,
          platformModules: [],
          eligibleProfiles: [],
          tenantOverride: null,
        });
      },
    }),
    {
      name: 'kanvara-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        forcePasswordChange: state.forcePasswordChange,
        workspace: state.workspace,
        platformModules: state.platformModules,
        tenantOverride: state.tenantOverride,
      }),
    }
  )
);

// AuthProvider for React context
const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const store = useAuthStore();

  // On boot, a persisted token means we have a session but no entitlements or
  // composed nav (neither survives a reload) — fetch them once.
  useEffect(() => {
    if (useAuthStore.getState().token) void refreshMe();
  }, []);

  return React.createElement(
    AuthContext.Provider,
    { value: store },
    children
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
}

// Step-up callback queue — pauses requests until MFA step-up completes
type StepUpCallback = { resolve: () => void; reject: (err: Error) => void };
let stepUpCallbacks: StepUpCallback[] = [];

function requestStepUp(): Promise<void> {
  return new Promise((resolve, reject) => {
    stepUpCallbacks.push({ resolve, reject });
    useAuthStore.getState().setStepUpRequired(true);
  });
}

export function completeStepUp() {
  const cbs = stepUpCallbacks;
  stepUpCallbacks = [];
  useAuthStore.getState().setStepUpRequired(false);
  cbs.forEach(cb => cb.resolve());
}

export function cancelStepUp() {
  const cbs = stepUpCallbacks;
  stepUpCallbacks = [];
  useAuthStore.getState().setStepUpRequired(false);
  cbs.forEach(cb => cb.reject(new Error('MFA step-up cancelled')));
}

// API client helper
export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const { token, tenantOverride } = useAuthStore.getState();

  // Don't set Content-Type for FormData - browser handles it automatically with correct boundary
  const isFormData = options?.body instanceof FormData;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  // Platform admins may act inside another tenant; the API honours this for
  // role 'admin' only and audits every use (middleware/auth.ts).
  if (tenantOverride) headers['X-Tenant-Id'] = tenantOverride;
  Object.assign(headers, (options?.headers ?? {}) as Record<string, string>);
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const text = await response.text();

  let data: { ok: boolean; data?: T; error?: { code: string; message: string } };
  try {
    data = JSON.parse(text) as { ok: boolean; data?: T; error?: { code: string; message: string } };
  } catch {
    throw new Error(`Invalid JSON response (status ${response.status}): ${text.slice(0, 200)}`);
  }

  if (!data.ok) {
    if (data.error?.code === 'MFA_STEP_UP_REQUIRED') {
      await requestStepUp();
      return apiRequest(endpoint, options);
    }
    if (data.error?.code === 'UNAUTHENTICATED' || response.status === 401) {
      useAuthStore.getState().logout();
      window.location.replace('/login?session=expired');
      throw new Error('Session expired — please log in again');
    }
    throw new Error(data.error?.message || 'API request failed');
  }

  return data.data as T;
}

/**
 * Download a binary endpoint (e.g. the .pptx case report) with the session's
 * auth headers and hand it to the browser as a file save.
 */
export async function apiDownload(endpoint: string, filename: string): Promise<void> {
  const { token, tenantOverride } = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (tenantOverride) headers['X-Tenant-Id'] = tenantOverride;
  const res = await fetch(`${API_BASE}${endpoint}`, { headers });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try { msg = (await res.json()).error?.message ?? msg; } catch { /* binary or empty */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Pull the current user's entitlements and composed workspace from /me and
 * push them into the store. Safe to call repeatedly; never throws.
 */
export async function refreshMe(): Promise<MeResponse | null> {
  try {
    const me = await apiRequest<MeResponse>('/me');
    const store = useAuthStore.getState();
    store.setWorkspace(me.workspace ?? null, me.platformModules ?? [], me.eligibleProfiles ?? []);
    if (me.role) {
      const current = store.user;
      if (current && (current.role !== me.role || current.name !== me.name)) {
        useAuthStore.setState({ user: { ...current, role: me.role, name: me.name } });
      }
    }
    return me;
  } catch {
    return null;
  }
}

// Login function
export async function login(email: string, password: string): Promise<{
  mfa_required?: boolean;
  challenge_id?: string;
  access_token?: string;
  refresh_token?: string;
  user?: User;
  force_password_change?: boolean;
}> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const text = await response.text();
  
  let data: { ok: boolean; data?: any; error?: { code: string; message: string } };
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Login failed: Invalid response (${response.status}): ${text.slice(0, 200)}`);
  }

  if (!data.ok) {
    throw new Error(data.error?.message || 'Login failed');
  }

  return data.data;
}

// MFA verify function
export async function verifyMfa(challengeId: string, code: string, isRecovery = false): Promise<{
  access_token: string;
  refresh_token: string;
  user: User;
  force_password_change?: boolean;
}> {
  const body = isRecovery
    ? { challenge_id: challengeId, recovery_code: code }
    : { challenge_id: challengeId, code };
  const response = await fetch(`${API_BASE}/auth/mfa/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const text = await response.text();
  let data: { ok: boolean; data?: any; error?: { code: string; message: string } };
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`MFA response invalid JSON (status ${response.status}): ${text.slice(0, 200)}`);
  }

  if (!data.ok) {
    throw new Error(data.error?.message || 'MFA verification failed');
  }

  return data.data;
}

// Logout function
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${useAuthStore.getState().token}`,
      },
    });
  } finally {
    useAuthStore.getState().logout();
  }
}
